import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { Card } from 'react-bootstrap';
import GenericButton from '../updated/shared/Button';
import OpenNotification from '../OpenNotification/OpenNotification';
import LoginRequiredPopup from '../updated/shared/LoginRequiredPopup';
import {
  getKybStatus,
  createApplication,
  updateBusinessInfo,
  uploadDocumentDirect,
  getApplicationDocuments,
  deleteKybDocument,
  addDirector,
  deleteDirector,
  getDirectors,
  addUBO,
  deleteUBO,
  getUBOs,
  updateUBOKycStatus,
  addTaxInfo,
  getTaxInfo,
  updateComplianceInfo,
  getComplianceInfo,
  submitKybApplication,
  type KybStatus,
  type KybApplicationStatus,
  type BusinessInfo,
  type Director,
  type DirectorPayload,
  type UBO,
  type UBOPayload,
  type TaxInfo,
  type ComplianceInfo,
  type KybDocumentType,
  type ApplicationDocument,
} from '../../services/kybApi';
import './VerifyWithKyb.css';

const steps = [
  'Business Information',
  'Corporate Documents',
  'Directors & UBOs',
  'Tax Information',
  'Bank Statement',
  'AML & Compliance',
  'Review & Submit',
  'Status',
];

const PRIMARY_CORPORATE_DOC_TYPES: KybDocumentType[] = [
  'CERTIFICATE_OF_INCORPORATION',
  'SHAREHOLDER_REGISTER',
  'BUSINESS_PROOF_OF_ADDRESS',
];

const ALL_KYB_DOCUMENT_TYPES: KybDocumentType[] = [
  'CERTIFICATE_OF_INCORPORATION',
  'SHAREHOLDER_REGISTER',
  'DIRECTOR_ID_FRONT',
  'DIRECTOR_ID_BACK',
  'BUSINESS_PROOF_OF_ADDRESS',
  'TAX_IDENTIFICATION',
  'COMPANY_BANK_STATEMENT',
  'AUTHORIZED_SIGNATORY_SELFIE',
];

const DOCUMENT_MAX_SIZE_MB = 10;
const DOCUMENT_MAX_SIZE_BYTES = DOCUMENT_MAX_SIZE_MB * 1024 * 1024;

const formatDocumentLabel = (docType: KybDocumentType) =>
  docType
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const DOCUMENT_TYPES_REQUIRING_BACK = new Set(['national_id', 'driving_license']);

const requiresBackDocument = (docType?: string | null) =>
  docType ? DOCUMENT_TYPES_REQUIRING_BACK.has(docType.toLowerCase()) : false;

const extractFileMeta = (dataUrl?: string | null) => {
  if (!dataUrl || typeof dataUrl !== 'string' || dataUrl.trim() === '') {
    return { data: null as string | null, mimeType: null as string | null };
  }
  const match = dataUrl.match(/^data:(.*?);base64,/);
  return {
    data: dataUrl,
    mimeType: match ? match[1] : null,
  };
};

const humanFileSize = (size: number) => {
  if (!size) {
    return '0 KB';
  }
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = size;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${unitIndex === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[unitIndex]}`;
};

const formatDocumentTimestamp = (timestamp?: string) => {
  if (!timestamp) {
    return 'Unknown time';
  }
  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) {
    return 'Unknown time';
  }
  return parsed.toLocaleString();
};

const normalizeDocumentType = (type?: string | null): KybDocumentType | null => {
  if (!type) {
    return null;
  }

  const cleaned = type
    .replace(/[-\s]+/g, '_')
    .replace(/__+/g, '_')
    .replace(/[^A-Z0-9_]/gi, '')
    .toUpperCase();

  if (ALL_KYB_DOCUMENT_TYPES.includes(cleaned as KybDocumentType)) {
    return cleaned as KybDocumentType;
  }

  return null;
};

const getDocumentDisplayLabel = (doc: ApplicationDocument) => {
  const normalizedType = normalizeDocumentType(doc.type);
  const fallbackLabel = doc.type
    .toLowerCase()
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
  const baseLabel = normalizedType ? formatDocumentLabel(normalizedType) : fallbackLabel;
  if (doc.side) {
    const sideLabel = doc.side === 'front' ? 'Front' : 'Back';
    return `${baseLabel} (${sideLabel})`;
  }
  return baseLabel;
};

const getBusinessOwnerEmail = () => {
  const email = localStorage.getItem('email');
  if (!email || email === 'undefined' || email === 'null') {
    return null;
  }
  return email;
};

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });

const VerifyWithKyb: React.FC = () => {
  // Log to confirm KYB component is loaded
  useEffect(() => {
    console.log('✅ KYB Component Loaded - Business Verification (KYB)');
  }, []);

  // Apply theme class based on userType (CaptainBee = yellow, Indexx Exchange = green)
  useEffect(() => {
    const applyTheme = () => {
      const userType = localStorage.getItem('userType');
      const userLogged = localStorage.getItem('userlogged');
      
      // Determine theme class based on userType or userLogged
      let themeClass = 'theme-normal'; // Default to green
      
      if (userType && userType !== 'Indexx Exchange') {
        // Any userType other than 'Indexx Exchange' is CaptainBee/HoneyUser
        themeClass = 'theme-captain'; // Yellow for CaptainBee
      } else if (userType === 'CaptainBee' || userLogged === 'captain') {
        themeClass = 'theme-captain'; // Yellow for CaptainBee
      } else if (userType === 'HoneyUser' || userLogged === 'honeyb') {
        themeClass = 'theme-honeyb'; // Yellow for HoneyBee
      } else if (userLogged === 'normal' || userType === 'Indexx Exchange') {
        themeClass = 'theme-normal'; // Green for normal users
      }
      
      // Apply theme class to document body and root
      document.body.classList.remove('theme-normal', 'theme-captain', 'theme-honeyb');
      document.body.classList.add(themeClass);
      document.documentElement.classList.remove('theme-normal', 'theme-captain', 'theme-honeyb');
      document.documentElement.classList.add(themeClass);
      
      // Also apply to root element to ensure CSS variables are available
      const root = document.getElementById('root');
      if (root) {
        root.classList.remove('theme-normal', 'theme-captain', 'theme-honeyb');
        root.classList.add(themeClass);
      }
    };

    // Apply theme on mount
    applyTheme();

    // Listen for storage changes and custom events
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'userType' || e.key === 'userlogged') {
        applyTheme();
      }
    };

    const handleUserTypeChange = () => {
      applyTheme();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userTypeChanged', handleUserTypeChange);

    // Poll for userType changes
    const pollInterval = setInterval(applyTheme, 500);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userTypeChanged', handleUserTypeChange);
      clearInterval(pollInterval);
    };
  }, []);

  const theme = useTheme();
  const navigate = useNavigate();
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  
  // Simple authentication check
  const checkAuthentication = useCallback(() => {
    const token = localStorage.getItem('access_token');
    return !!(token && token !== 'null' && token !== 'undefined' && token.trim() !== '');
  }, []);
  
  const showLoginRequiredPopup = useCallback(() => {
    setShowLoginPopup(true);
  }, []);
  
  const handleLoginPopupClose = useCallback(() => {
    setShowLoginPopup(false);
  }, []);
  
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [applicationId, setApplicationId] = useState<string | null>(null);
  const [kybStatus, setKybStatus] = useState<KybStatus | null>(null);
  const [shouldPollKybStatus, setShouldPollKybStatus] = useState(true);

  // Business Information State
  const [businessInfo, setBusinessInfo] = useState<BusinessInfo>({
    legalName: '',
    tradingName: '',
    registrationNumber: '',
    countryOfIncorporation: '',
    address: '',
    website: '',
    businessType: '',
    industryCode: '',
    yearOfIncorporation: '',
    numberOfEmployees: '',
    businessDescription: '',
  });
  
  // Documents State
  const [documents, setDocuments] = useState<{
    [key in KybDocumentType]?: { file: File | null; uploadStatus: 'idle' | 'uploading' | 'success' | 'error'; s3Key: string | null };
  }>({});
  const [fetchedDocuments, setFetchedDocuments] = useState<ApplicationDocument[]>([]);
  const [deletingDocumentIds, setDeletingDocumentIds] = useState<Record<string, boolean>>({});
  const isDocumentProvided = useCallback(
    (docType: KybDocumentType) => {
      const docState = documents[docType];
      const hasLocal = docState?.uploadStatus === 'success' || !!docState?.s3Key;
      const hasFetched = fetchedDocuments.some(
        (doc) =>
          doc.type === docType ||
          doc.type?.toUpperCase() === docType.toUpperCase() ||
          doc.type?.replace(/-/g, '_').toUpperCase() === docType.toUpperCase()
      );

      return hasLocal || hasFetched;
    },
    [documents, fetchedDocuments]
  );
  
  // Directors State
  const [directors, setDirectors] = useState<Director[]>([]);
  const [directorForm, setDirectorForm] = useState<Omit<Director, '_id' | 'kybApplicationId' | 'userId' | 'createdAt' | 'updatedAt'>>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    nationality: '',
    address: '',
    countryOfResidence: '',
    position: '',
    ownershipPercentage: 0,
    idDocumentType: 'passport',
    idDocumentFront: '',
    idDocumentBack: '',
    selfieImage: '',
    pepStatus: 'no',
    sanctionsStatus: 'no',
  });
  const [showDirectorForm, setShowDirectorForm] = useState(false);

  // UBOs State
  const [ubos, setUbos] = useState<UBO[]>([]);
  const [uboForm, setUboForm] = useState<Omit<UBO, '_id' | 'kybApplicationId' | 'userId' | 'kycStatus' | 'kycApplicationId' | 'createdAt' | 'updatedAt'>>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    nationality: '',
    address: '',
    countryOfResidence: '',
    ownershipPercentage: 0,
    relationshipToCompany: 'Shareholder',
    idDocumentType: 'passport',
    idDocumentFront: '',
    idDocumentBack: '',
    selfieImage: '',
    pepStatus: 'no',
    sanctionsStatus: 'no',
  });
  const [showUboForm, setShowUboForm] = useState(false);

  // Tax Information State
  const [taxInfo, setTaxInfo] = useState<TaxInfo | null>(null);
  const [taxForm, setTaxForm] = useState<Omit<TaxInfo, '_id' | 'kybApplicationId' | 'userId' | 'createdAt' | 'updatedAt'>>({
    taxIdentificationNumber: '',
    taxCountry: '',
    vatNumber: '',
    fatcaStatus: 'not_applicable',
  });
  const [complianceInfo, setComplianceInfo] = useState<ComplianceInfo | null>(null);
  const [complianceForm, setComplianceForm] = useState({
    purposeOfUse: '',
    expectedMonthlyVolume: '',
    expectedTransactionCount: '',
    expectedTransactionSize: '',
    sourceOfFunds: '',
    sourceOfWealth: '',
    dealsWithCrypto: 'no',
    dealsWithCashIntensive: 'no',
    dealsWithHighRiskCountries: 'no',
  });

  // Reapply state removed so applications can resubmit immediately
  // Check authentication on mount
  useEffect(() => {
    if (!checkAuthentication()) {
      showLoginRequiredPopup();
    }
  }, [checkAuthentication, showLoginRequiredPopup]);

  // Load existing KYB status on mount
  useEffect(() => {
    if (checkAuthentication()) {
      loadKybStatus();
    }
  }, [checkAuthentication]);

  // Reload documents when entering the documents step
  useEffect(() => {
    if (activeStep === 1 && applicationId) {
      loadApplicationDocuments(applicationId);
    }
  }, [activeStep, applicationId]);

  // Reload directors and UBOs when entering step 2
  useEffect(() => {
    if (activeStep === 2 && applicationId) {
      loadDirectors(applicationId);
      loadUBOs(applicationId);
    }
  }, [activeStep, applicationId]);

  useEffect(() => {
    if (activeStep === 5 && applicationId) {
      loadComplianceInfo(applicationId);
    }
  }, [activeStep, applicationId]);

  const loadKybStatus = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token || token === 'null' || token === 'undefined' || token.trim() === '') {
        console.error('❌ No valid access token found');
        OpenNotification('error', 'Please log in to continue with KYB verification.');
        showLoginRequiredPopup();
      return;
    }

      const response = await getKybStatus();
      const status = (response as any).data?.data || (response as any).data;
      setKybStatus(status);
      const currentStatus = status?.application?.status;
      setShouldPollKybStatus(currentStatus !== 'rejected');

      if (status?.application?._id) {
        setApplicationId(status.application._id);
        
        // Load business info if exists
        if (status.application.businessInfo) {
          setBusinessInfo((prev) => ({
            ...prev,
            ...status.application.businessInfo,
          }));
        }

        // Load documents
        loadApplicationDocuments(status.application._id);
        
        // Load directors
        loadDirectors(status.application._id);
        
        // Load UBOs
        loadUBOs(status.application._id);
        
        // Load tax info
        loadTaxInfo(status.application._id);
        
        // Load compliance info
        loadComplianceInfo(status.application._id);

        // Navigate to appropriate step based on status
        if (status.application.status !== 'draft' && status.application.status !== 'need_more_info') {
          setActiveStep(7); // Status step
        } else if (status.application.status === 'need_more_info') {
          // Determine which step to show based on what's missing
          if (!status.application.businessInfo) {
            setActiveStep(0);
          } else if (status.application.documentsCount === 0) {
            setActiveStep(1);
          } else if (status.application.directorsCount === 0) {
            setActiveStep(2);
          } else if (!status.application.hasTaxInfo) {
            setActiveStep(3);
          } else {
            setActiveStep(5); // AML & Compliance step by default when more info needed
          }
        }

        // Dispatch event when KYB is approved or rejected (for UI updates)
        // Show notifications and trigger email (backend should send email automatically)
        if (status.application.status === 'approved') {
          OpenNotification('success', '🎉 Your KYB verification has been approved! You now have access to advanced business features.');
          window.dispatchEvent(new Event('kybStatusUpdated'));
          console.log('✅ KYB Approved - Event dispatched');
          // Note: Email notification should be sent by backend when admin approves
        } else if (status.application.status === 'rejected') {
          const rejectionMsg = status.application.rejectionReason 
            ? `Your KYB application has been rejected. Reason: ${status.application.rejectionReason}`
            : 'Your KYB application has been rejected. Please review and resubmit.';
          OpenNotification('error', rejectionMsg);
          window.dispatchEvent(new Event('kybStatusUpdated'));
          console.log('❌ KYB Rejected - Event dispatched');
          // Note: Email notification should be sent by backend when admin rejects
        }
      }
    } catch (error: any) {
      console.error('Failed to load KYB status:', error);
      if (error.response?.status === 401 || error.status === 401) {
        OpenNotification('error', 'Your session has expired. Please log in again.');
        localStorage.removeItem('access_token');
        showLoginRequiredPopup();
      }
    }
  };

  // Poll for KYB status updates when on status page and not yet approved/rejected
  useEffect(() => {
    const shouldPoll =
      activeStep === 7 &&
      applicationId &&
      shouldPollKybStatus &&
      kybStatus?.application?.status &&
      kybStatus.application.status !== 'approved' &&
      kybStatus.application.status !== 'rejected';

    if (!shouldPoll) return;

    // Poll every 30 seconds to check if admin has approved/rejected
    const pollInterval = setInterval(async () => {
      try {
        const response = await getKybStatus();
        const status = (response as any).data?.data || (response as any).data;
        if (status && status.application) {
          const previousStatus = kybStatus?.application?.status;
          setKybStatus(status);
          setShouldPollKybStatus(status.application.status !== 'rejected');

          // If status changed to approved or rejected, trigger updates and show notifications
          if (status.application.status === 'approved' && previousStatus !== 'approved') {
            OpenNotification('success', '🎉 Your KYB verification has been approved! You now have access to advanced business features.');
            window.dispatchEvent(new Event('kybStatusUpdated'));
            console.log('✅ KYB Approved via polling - Event dispatched');
            clearInterval(pollInterval);
          } else if (status.application.status === 'rejected' && previousStatus !== 'rejected') {
            const rejectionMsg = status.application.rejectionReason 
              ? `Your KYB application has been rejected. Reason: ${status.application.rejectionReason}`
              : 'Your KYB application has been rejected. Please review and resubmit.';
            OpenNotification('error', rejectionMsg);
            window.dispatchEvent(new Event('kybStatusUpdated'));
            console.log('❌ KYB Rejected via polling - Event dispatched');
            clearInterval(pollInterval);
          }
        }
      } catch (error) {
        // Silently fail - status endpoint might not exist
        console.log('Status polling failed, endpoint may not exist');
      }
    }, 30000); // Poll every 30 seconds

    return () => clearInterval(pollInterval);
  }, [activeStep, applicationId, kybStatus?.application?.status, shouldPollKybStatus]);

  const loadApplicationDocuments = async (appId: string) => {
    try {
      const response = await getApplicationDocuments(appId);
      const docs =
        (response as any).data?.data?.documents ||
        (response as any).data?.documents ||
        response.data?.documents ||
        [];

      if (Array.isArray(docs)) {
        setFetchedDocuments(docs);

        setDocuments((prev) => {
          const next = { ...prev };
          docs.forEach((doc) => {
            const normalizedType = (doc.type || '')
              .replace(/-/g, '_')
              .toUpperCase();

            if (ALL_KYB_DOCUMENT_TYPES.includes(normalizedType as KybDocumentType)) {
              const castedType = normalizedType as KybDocumentType;
              const existing = next[castedType];
              next[castedType] = {
                file: null,
                uploadStatus: 'success',
                s3Key: doc.s3Key || doc._id || existing?.s3Key || null,
              };
            }
          });
          return next;
        });
      } else {
        setFetchedDocuments([]);
      }
    } catch (error: any) {
      if (error.response?.status !== 404) {
        console.error('Failed to load application documents:', error);
      }
    }
  };

  const handleDeleteDocument = async (doc: ApplicationDocument) => {
    if (!applicationId) {
      OpenNotification('error', 'No application found to delete this document.');
      return;
    }

    if (deletingDocumentIds[doc._id]) {
      return;
    }

    setDeletingDocumentIds((prev) => ({ ...prev, [doc._id]: true }));
    try {
      await deleteKybDocument(applicationId, doc._id);
      const label = getDocumentDisplayLabel(doc);
      OpenNotification('success', `${label} removed successfully`);

      setDocuments((prev) => {
        const next = { ...prev };
        const normalizedType = normalizeDocumentType(doc.type);
        if (normalizedType) {
          delete next[normalizedType];
        }
        return next;
      });

      await loadApplicationDocuments(applicationId);
    } catch (error: any) {
      console.error('Failed to delete document:', error);
      const errorMessage = error?.response?.data?.message || error.message || 'Failed to delete document';
      OpenNotification('error', errorMessage);
    } finally {
      setDeletingDocumentIds((prev) => {
        const next = { ...prev };
        delete next[doc._id];
        return next;
      });
    }
  };

  const loadDirectors = async (appId: string) => {
    try {
      const response = await getDirectors(appId);
      // Handle nested response structure
      const directorsData = (response as any).data?.data?.directors || (response as any).data?.directors || response.data?.directors;
      if (directorsData && Array.isArray(directorsData)) {
        console.log('✅ Loaded directors:', directorsData);
        setDirectors(directorsData);
      } else {
        console.log('No directors found or invalid response structure:', response);
        setDirectors([]);
      }
    } catch (error: any) {
      if (error.response?.status !== 404) {
        console.error('Failed to load directors:', error);
      } else {
        // 404 is normal when no directors exist yet
        setDirectors([]);
      }
    }
  };

  const loadUBOs = async (appId: string) => {
    try {
      const response = await getUBOs(appId);
      // Handle nested response structure
      const ubosData = (response as any).data?.data?.ubos || (response as any).data?.ubos || response.data?.ubos;
      if (ubosData && Array.isArray(ubosData)) {
        console.log('✅ Loaded UBOs:', ubosData);
        setUbos(ubosData);
      } else {
        console.log('No UBOs found or invalid response structure:', response);
        setUbos([]);
      }
    } catch (error: any) {
      if (error.response?.status !== 404) {
        console.error('Failed to load UBOs:', error);
      } else {
        // 404 is normal when no UBOs exist yet
        setUbos([]);
      }
    }
  };

  const loadTaxInfo = async (appId: string) => {
    try {
      const response = await getTaxInfo(appId);
      if (response.data) {
        setTaxInfo(response.data);
        if (response.data) {
          setTaxForm({
            taxIdentificationNumber: response.data.taxIdentificationNumber || '',
            taxCountry: response.data.taxCountry || '',
            vatNumber: response.data.vatNumber || '',
            fatcaStatus: response.data.fatcaStatus || 'not_applicable',
          });
        }
      }
    } catch (error: any) {
      if (error.response?.status !== 404) {
        console.error('Failed to load tax info:', error);
      }
    }
  };

  const loadComplianceInfo = async (appId: string) => {
    try {
      const response = await getComplianceInfo(appId);
      const payload = (response as any).data?.data || response.data;
      if (payload) {
        const data = payload;
        setComplianceInfo(data);
        setComplianceForm({
          purposeOfUse: data.purposeOfUse || '',
          expectedMonthlyVolume: data.expectedMonthlyVolume || '',
          expectedTransactionCount: data.expectedTransactionCount || '',
          expectedTransactionSize: data.expectedTransactionSize || '',
          sourceOfFunds: data.sourceOfFunds || '',
          sourceOfWealth: data.sourceOfWealth || '',
          dealsWithCrypto: data.dealsWithCrypto ? 'yes' : 'no',
          dealsWithCashIntensive: data.dealsWithCashIntensive ? 'yes' : 'no',
          dealsWithHighRiskCountries: data.dealsWithHighRiskCountries ? 'yes' : 'no',
        });
      }
    } catch (error: any) {
      if (error.response?.status !== 404) {
        console.error('Failed to load compliance info:', error);
      } else {
        setComplianceInfo(null);
      }
    }
  };

  // Step 1: Create application and save business info
  const handleBusinessInfoSubmit = async () => {
    if (
      !businessInfo.legalName ||
      !businessInfo.registrationNumber ||
      !businessInfo.countryOfIncorporation ||
      !businessInfo.address ||
      !businessInfo.businessType ||
      !businessInfo.industryCode ||
      !businessInfo.yearOfIncorporation ||
      !businessInfo.numberOfEmployees ||
      !businessInfo.businessDescription
    ) {
      OpenNotification('error', 'Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      let appId = applicationId;

      // Create application if it doesn't exist
      if (!appId) {
        const createResponse = await createApplication({
          country: businessInfo.countryOfIncorporation,
        });
        // Handle nested response structure: response.data.data._id or response.data._id
        const responseData = (createResponse as any).data?.data || (createResponse as any).data;
        appId = responseData?._id;
        
        if (!appId) {
          console.error('Failed to get application ID from response:', createResponse);
          OpenNotification('error', 'Failed to create application. Please try again.');
          setLoading(false);
          return;
        }
        
        setApplicationId(appId);
        console.log('✅ Created application with ID:', appId);
      }

      // Ensure appId is valid before updating
      if (!appId || appId === 'undefined' || appId === 'null') {
        console.error('Invalid application ID:', appId);
        OpenNotification('error', 'Invalid application ID. Please refresh and try again.');
        setLoading(false);
      return;
    }

      // Update business info
      await updateBusinessInfo(appId, businessInfo);
      OpenNotification('success', 'Business information saved successfully');
      setActiveStep(1); // Move to documents step
    } catch (error: any) {
      console.error('Failed to save business info:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save business information';
      OpenNotification('error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Upload documents
  const handleDocumentUpload = async (type: KybDocumentType, file: File) => {
    if (!applicationId) {
      OpenNotification('error', 'Please complete business information first');
      return;
    }

    // Check file size (10MB limit for original file)
    if (file.size > DOCUMENT_MAX_SIZE_BYTES) {
      OpenNotification('error', `File size must be less than ${DOCUMENT_MAX_SIZE_MB}MB. Please compress the file or use a smaller one.`);
        return;
      }
      
    setDocuments(prev => ({
      ...prev,
      [type]: { ...prev[type], file, uploadStatus: 'uploading', s3Key: null },
    }));

    try {
      const response = await uploadDocumentDirect(applicationId, type, file);
      const docData = (response as any).data?.data || response.data;

      setDocuments(prev => ({
        ...prev,
        [type]: { ...prev[type], uploadStatus: 'success', s3Key: docData.s3Key || docData._id },
      }));

      // Reload documents list
      await loadApplicationDocuments(applicationId);
      OpenNotification('success', 'Document uploaded successfully');
    } catch (error: any) {
      console.error('Failed to upload document:', error);
      setDocuments(prev => ({
        ...prev,
        [type]: { ...prev[type], uploadStatus: 'error', s3Key: null },
      }));
      
      // Show user-friendly error message
      let errorMessage = 'Failed to upload document';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.status === 413) {
        errorMessage = 'File is too large. Please compress the image or use a smaller file (max 2-3MB recommended).';
      }
      
      OpenNotification('error', errorMessage);
    }
  };

  // Step 3: Add Director
  const handleAddDirector = async () => {
      if (!applicationId) {
      OpenNotification('error', 'Please complete previous steps first');
        return;
    }

    const missingTextFields = [
      directorForm.firstName,
      directorForm.lastName,
      directorForm.email,
      directorForm.position,
      directorForm.dateOfBirth,
      directorForm.nationality,
      directorForm.countryOfResidence,
      directorForm.address,
      directorForm.idDocumentType,
      directorForm.pepStatus,
      directorForm.sanctionsStatus,
    ];

    if (missingTextFields.some((field) => !field || (typeof field === 'string' && field.trim() === ''))) {
      OpenNotification('error', 'Please complete all director identity, document, and compliance fields.');
      return;
    }

    const normalizedDocType = (directorForm.idDocumentType || 'passport').toLowerCase();
    const requiresBackSide = requiresBackDocument(normalizedDocType);

    if (!directorForm.idDocumentFront) {
      OpenNotification('error', 'Please upload the front of the selected ID document.');
      return;
    }

    if (requiresBackSide && !directorForm.idDocumentBack) {
      OpenNotification('error', 'Please upload the back of the selected ID document.');
      return;
    }

    setLoading(true);
    try {
      const ownerEmail = getBusinessOwnerEmail();
      const frontMeta = extractFileMeta(directorForm.idDocumentFront);
      const backMeta = extractFileMeta(directorForm.idDocumentBack);
      const selfieMeta = extractFileMeta(directorForm.selfieImage);
      const isPassport = normalizedDocType === 'passport';

      const directorPayload: DirectorPayload = {
        businessOwnerEmail: ownerEmail,
        firstName: directorForm.firstName.trim(),
        lastName: directorForm.lastName.trim(),
        email: directorForm.email.trim(),
        phone: directorForm.phone || undefined,
        dateOfBirth: directorForm.dateOfBirth,
        nationality: directorForm.nationality,
        countryOfResidence: directorForm.countryOfResidence,
        address: directorForm.address,
        position: directorForm.position,
        ownershipPercentage: directorForm.ownershipPercentage || 0,
        idDocumentType: directorForm.idDocumentType || 'passport',
        idDocumentImage: isPassport ? frontMeta.data : null,
        idDocumentMimeType: isPassport ? frontMeta.mimeType : null,
        idDocumentFrontImage: isPassport ? null : frontMeta.data,
        idDocumentFrontMimeType: isPassport ? null : frontMeta.mimeType,
        idDocumentBackImage: requiresBackSide ? backMeta.data : null,
        idDocumentBackMimeType: requiresBackSide ? backMeta.mimeType : null,
        selfieImage: selfieMeta.data,
        selfieMimeType: selfieMeta.mimeType,
        isPep: directorForm.pepStatus === 'yes',
        isSanctioned: directorForm.sanctionsStatus === 'yes',
      };

      console.log('Adding director payload:', directorPayload);
      const response = await addDirector(applicationId, directorPayload);
      console.log('Director add response:', response);
      
      OpenNotification('success', 'Director added successfully');
      
      // Reset form
      setDirectorForm({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        dateOfBirth: '',
        nationality: '',
        address: '',
        countryOfResidence: '',
        position: '',
        ownershipPercentage: 0,
        idDocumentType: 'passport',
        idDocumentFront: '',
        idDocumentBack: '',
        selfieImage: '',
        pepStatus: 'no',
        sanctionsStatus: 'no',
      });
      setShowDirectorForm(false);
      
      // Reload directors list
      await loadDirectors(applicationId);
      
      // Also add to local state immediately if response has the director
      const newDirector = (response as any).data?.data || (response as any).data;
      if (newDirector && newDirector._id) {
        setDirectors(prev => [...prev, newDirector]);
      }
    } catch (error: any) {
      console.error('Failed to add director:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to add director';
      OpenNotification('error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDirector = async (directorId: string) => {
    if (!directorId) return;
    
    setLoading(true);
    try {
      await deleteDirector(directorId);
      OpenNotification('success', 'Director deleted successfully');
      if (applicationId) {
        await loadDirectors(applicationId);
      }
    } catch (error: any) {
      console.error('Failed to delete director:', error);
      OpenNotification('error', error.response?.data?.message || 'Failed to delete director');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Add UBO
  const handleAddUBO = async () => {
    if (!applicationId) {
      OpenNotification('error', 'Please complete previous steps first');
        return;
      }

    const requiredUboFields = [
      uboForm.firstName,
      uboForm.lastName,
      uboForm.email,
      uboForm.ownershipPercentage,
      uboForm.relationshipToCompany,
      uboForm.dateOfBirth,
      uboForm.nationality,
      uboForm.countryOfResidence,
      uboForm.address,
      uboForm.idDocumentType,
      uboForm.pepStatus,
      uboForm.sanctionsStatus,
    ];

    if (requiredUboFields.some((field) => field === undefined || field === null || (typeof field === 'string' && field.toString().trim() === ''))) {
      OpenNotification('error', 'Please complete all UBO identity, ownership, and compliance fields.');
      return;
    }

    const normalizedDocType = (uboForm.idDocumentType || 'passport').toLowerCase();
    const requiresBackSide = requiresBackDocument(normalizedDocType);

    if (!uboForm.idDocumentFront) {
      OpenNotification('error', 'Please upload the front of the selected ID document for the UBO.');
      return;
    }

    if (requiresBackSide && !uboForm.idDocumentBack) {
      OpenNotification('error', 'Please upload the back of the selected ID document for the UBO.');
      return;
    }
    
    setLoading(true);
    try {
      const ownerEmail = getBusinessOwnerEmail();
      const frontMeta = extractFileMeta(uboForm.idDocumentFront);
      const backMeta = extractFileMeta(uboForm.idDocumentBack);
      const selfieMeta = extractFileMeta(uboForm.selfieImage);
      const isPassport = normalizedDocType === 'passport';

      const uboPayload: UBOPayload = {
        businessOwnerEmail: ownerEmail,
        firstName: uboForm.firstName.trim(),
        lastName: uboForm.lastName.trim(),
        email: uboForm.email.trim(),
        phone: uboForm.phone || undefined,
        dateOfBirth: uboForm.dateOfBirth,
        nationality: uboForm.nationality,
        countryOfResidence: uboForm.countryOfResidence,
        address: uboForm.address,
        ownershipPercentage: uboForm.ownershipPercentage || 0,
        relationshipToCompany: uboForm.relationshipToCompany,
        idDocumentType: uboForm.idDocumentType || 'passport',
        idDocumentImage: isPassport ? frontMeta.data : null,
        idDocumentMimeType: isPassport ? frontMeta.mimeType : null,
        idDocumentFrontImage: isPassport ? null : frontMeta.data,
        idDocumentFrontMimeType: isPassport ? null : frontMeta.mimeType,
        idDocumentBackImage: requiresBackSide ? backMeta.data : null,
        idDocumentBackMimeType: requiresBackSide ? backMeta.mimeType : null,
        selfieImage: selfieMeta.data,
        selfieMimeType: selfieMeta.mimeType,
        isPep: uboForm.pepStatus === 'yes',
        isSanctioned: uboForm.sanctionsStatus === 'yes',
      };

      console.log('Adding UBO payload:', uboPayload);
      const response = await addUBO(applicationId, uboPayload);
      console.log('UBO add response:', response);
      
      OpenNotification('success', 'UBO added successfully');
      
      // Reset form
      setUboForm({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        dateOfBirth: '',
        nationality: '',
        address: '',
        countryOfResidence: '',
        ownershipPercentage: 0,
        relationshipToCompany: 'Shareholder',
        idDocumentType: 'passport',
        idDocumentFront: '',
        idDocumentBack: '',
        selfieImage: '',
        pepStatus: 'no',
        sanctionsStatus: 'no',
      });
      setShowUboForm(false);
      
      // Reload UBOs list
      await loadUBOs(applicationId);
      
      // Also add to local state immediately if response has the UBO
      const newUBO = (response as any).data?.data || (response as any).data;
      if (newUBO && newUBO._id) {
        setUbos(prev => [...prev, newUBO]);
          }
        } catch (error: any) {
      console.error('Failed to add UBO:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to add UBO';
      OpenNotification('error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUBO = async (uboId: string) => {
    if (!uboId) return;
    
    setLoading(true);
    try {
      await deleteUBO(uboId);
      OpenNotification('success', 'UBO deleted successfully');
      if (applicationId) {
        await loadUBOs(applicationId);
      }
    } catch (error: any) {
      console.error('Failed to delete UBO:', error);
      OpenNotification('error', error.response?.data?.message || 'Failed to delete UBO');
    } finally {
      setLoading(false);
    }
  };

  // Step 4: Update UBO KYC Status (when UBO completes their KYC)
  const handleUBOKycComplete = async (uboId: string, kycApplicationId: string) => {
    setLoading(true);
    try {
      await updateUBOKycStatus(uboId, 'completed', kycApplicationId);
      OpenNotification('success', 'UBO KYC status updated');
      if (applicationId) {
        await loadUBOs(applicationId);
      }
    } catch (error: any) {
      console.error('Failed to update UBO KYC status:', error);
      OpenNotification('error', error.response?.data?.message || 'Failed to update UBO KYC status');
    } finally {
      setLoading(false);
    }
  };

  // Step 5: Add Tax Information
  const handleTaxInfoSubmit = async () => {
    if (!applicationId) {
      OpenNotification('error', 'Please complete previous steps first');
      return;
    }

    if (!taxForm.taxIdentificationNumber || !taxForm.taxCountry || !taxForm.fatcaStatus) {
      OpenNotification('error', 'Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await addTaxInfo(applicationId, taxForm);
      OpenNotification('success', 'Tax information saved successfully');
      await loadTaxInfo(applicationId);
      setActiveStep(4); // Move to bank statement step
    } catch (error: any) {
      console.error('Failed to save tax info:', error);
      OpenNotification('error', error.response?.data?.message || 'Failed to save tax information');
    } finally {
      setLoading(false);
        }
  };

  // Step 7: Submit Application
  const handleSubmitApplication = async () => {
    if (!applicationId) {
      OpenNotification('error', 'No application found');
      return;
    }

    // Validate all required steps are completed
    if (!businessInfo.legalName) {
      OpenNotification('error', 'Please complete business information');
      return;
    }

    if (directors.length === 0) {
      OpenNotification('error', 'Please add at least one director');
      return;
    }

    // Note: UBO KYC completion is no longer required as a separate step

    setLoading(true);
    try {
      const response = await submitKybApplication(applicationId);
      OpenNotification('success', 'Application submitted successfully');
      
      // Update status
      await loadKybStatus();
      setActiveStep(7); // Move to status step
      
      // Dispatch event for status update
      const responseData = (response as any).data?.data || response.data;
      if (responseData?.status === 'approved') {
        window.dispatchEvent(new Event('kybStatusUpdated'));
        console.log('✅ KYB Application Submitted and Approved - Event dispatched');
      } else {
        window.dispatchEvent(new Event('kybStatusUpdated'));
        console.log('📋 KYB Application Submitted - Event dispatched');
      }
      
      // Note: Email notification will be sent by backend when admin approves/rejects the application
    } catch (error: any) {
      console.error('Failed to submit application:', error);
      OpenNotification('error', error.response?.data?.message || 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  const handleComplianceSubmit = async () => {
    if (!applicationId) {
      OpenNotification('error', 'Please complete previous steps first');
      return;
    }

    const requiredFields = [
      complianceForm.purposeOfUse,
      complianceForm.expectedMonthlyVolume,
      complianceForm.expectedTransactionCount,
      complianceForm.expectedTransactionSize,
      complianceForm.sourceOfFunds,
      complianceForm.sourceOfWealth,
      complianceForm.dealsWithCrypto,
      complianceForm.dealsWithCashIntensive,
      complianceForm.dealsWithHighRiskCountries,
    ];

    if (requiredFields.some((field) => !field || field.trim() === '')) {
      OpenNotification('error', 'Please answer all AML/Compliance questions.');
      return;
    }

    const payload = {
      purposeOfUse: complianceForm.purposeOfUse,
      expectedMonthlyVolume: complianceForm.expectedMonthlyVolume,
      expectedTransactionCount: complianceForm.expectedTransactionCount,
      expectedTransactionSize: complianceForm.expectedTransactionSize,
      sourceOfFunds: complianceForm.sourceOfFunds,
      sourceOfWealth: complianceForm.sourceOfWealth,
      dealsWithCrypto: complianceForm.dealsWithCrypto === 'yes',
      dealsWithCashIntensive: complianceForm.dealsWithCashIntensive === 'yes',
      dealsWithHighRiskCountries: complianceForm.dealsWithHighRiskCountries === 'yes',
    };

    setLoading(true);
    try {
      await updateComplianceInfo(applicationId, payload);
      OpenNotification('success', 'Compliance information saved successfully');
      await loadComplianceInfo(applicationId);
      setActiveStep(6); // Move to review step
    } catch (error: any) {
      console.error('Failed to save compliance info:', error);
      OpenNotification('error', error.response?.data?.message || 'Failed to save compliance information');
    } finally {
      setLoading(false);
    }
  };

  const handleReapply = () => {
    setActiveStep(0);
    setApplicationId(null);
    setKybStatus(null);
    setBusinessInfo({
      legalName: '',
      tradingName: '',
      registrationNumber: '',
      countryOfIncorporation: '',
      address: '',
      website: '',
      businessType: '',
      industryCode: '',
      yearOfIncorporation: '',
      numberOfEmployees: '',
      businessDescription: '',
    });
    setDocuments({});
    setDirectors([]);
    setUbos([]);
    setTaxInfo(null);
    setTaxForm({
      taxIdentificationNumber: '',
      taxCountry: '',
      vatNumber: '',
      fatcaStatus: 'not_applicable',
    });
    setComplianceInfo(null);
    setComplianceForm({
      purposeOfUse: '',
      expectedMonthlyVolume: '',
      expectedTransactionCount: '',
      expectedTransactionSize: '',
      sourceOfFunds: '',
      sourceOfWealth: '',
      dealsWithCrypto: 'no',
      dealsWithCashIntensive: 'no',
      dealsWithHighRiskCountries: 'no',
    });
    setShouldPollKybStatus(true);
  };

  const handleNext = () => {
    if (activeStep === 0) {
      handleBusinessInfoSubmit();
    } else if (activeStep === 1) {
      const missingDocuments = PRIMARY_CORPORATE_DOC_TYPES.filter((docType) => !isDocumentProvided(docType));

      if (missingDocuments.length > 0) {
        OpenNotification(
          'error',
          `Please upload all required documents: ${missingDocuments.map((doc) => formatDocumentLabel(doc)).join(', ')}`
        );
        return;
      }
      setActiveStep(2);
    } else if (activeStep === 2) {
      // Check if at least one director is added
      if (directors.length === 0) {
        OpenNotification('error', 'Please add at least one director');
        return;
      }
      setActiveStep(3); // Move to Tax Information (previously step 4)
    } else if (activeStep === 3) {
      handleTaxInfoSubmit();
    } else if (activeStep === 4) {
      // Bank statement step - check if uploaded
      const hasBankStatement = documents.COMPANY_BANK_STATEMENT?.uploadStatus === 'success' ||
        fetchedDocuments.some(doc => doc.type === 'COMPANY_BANK_STATEMENT');
      if (!hasBankStatement) {
        OpenNotification('error', 'Please upload business bank statement');
        return;
      }
      setActiveStep(5);
    } else if (activeStep === 5) {
      handleComplianceSubmit();
    } else if (activeStep === 6) {
      handleSubmitApplication();
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const getDocumentsForType = (docType: KybDocumentType) =>
    fetchedDocuments.filter((doc) => normalizeDocumentType(doc.type) === docType);

  const renderDocumentInlineActions = (docType: KybDocumentType) => {
    const docs = getDocumentsForType(docType);
    if (docs.length === 0) {
      return null;
    }

    return (
      <div className="kyc-document-inline-actions">
        {docs.map((doc) => {
          const metaParts = [
            doc.side ? (doc.side === 'front' ? 'Front side' : 'Back side') : '',
            formatDocumentTimestamp(doc.createdAt),
            humanFileSize(doc.fileSize),
          ].filter(Boolean);

          return (
            <div key={doc._id} className="kyc-document-inline-row">
              <div className="kyc-document-inline-meta">
                <strong>{getDocumentDisplayLabel(doc)}</strong>
                <span className="kyc-document-inline-subtitle">{metaParts.join(' · ')}</span>
              </div>
              <div className="kyc-document-inline-actions-group">
                <GenericButton
                  text="View"
                  onClick={() => {
                    if (doc.viewUrl) {
                      window.open(doc.viewUrl, '_blank', 'noopener');
                    } else {
                      OpenNotification('error', 'Document preview is not available yet.');
                    }
                  }}
                  disabled={!doc.viewUrl}
                  className="kyc-document-view-button"
                  styles={{ width: 'auto', minWidth: '100px', padding: '6px 14px' }}
                />
                <GenericButton
                  text="Delete"
                  onClick={() => handleDeleteDocument(doc)}
                  loading={!!deletingDocumentIds[doc._id]}
                  disabled={!!deletingDocumentIds[doc._id]}
                  className="kyc-document-delete-button"
                  styles={{ width: 'auto', minWidth: '120px', padding: '6px 12px' }}
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <div className="kyc-step-content">
            <h3 className="kyc-step-title">Business Information</h3>
            <p className="kyc-step-subtitle">Please provide your business details</p>
              
            <div className="kyc-form-section">
              <TextField
                label="Legal Name *"
                value={businessInfo.legalName}
                onChange={(e) => setBusinessInfo(prev => ({ ...prev, legalName: e.target.value }))}
                fullWidth
                required
                className="kyc-form-field"
                margin="normal"
              />
                  <TextField
                label="Trading Name (Optional)"
                value={businessInfo.tradingName}
                onChange={(e) => setBusinessInfo(prev => ({ ...prev, tradingName: e.target.value }))}
                fullWidth
                className="kyc-form-field"
                margin="normal"
              />
              <TextField
                label="Registration Number *"
                value={businessInfo.registrationNumber}
                onChange={(e) => setBusinessInfo(prev => ({ ...prev, registrationNumber: e.target.value }))}
                    fullWidth
                    required
                    className="kyc-form-field"
                margin="normal"
                  />
              <TextField
                label="Country of Incorporation *"
                value={businessInfo.countryOfIncorporation}
                onChange={(e) => setBusinessInfo(prev => ({ ...prev, countryOfIncorporation: e.target.value }))}
                fullWidth
                required
                className="kyc-form-field"
                margin="normal"
                placeholder="e.g., US, UK, PK"
              />
              <TextField
                label="Business Address *"
                value={businessInfo.address}
                onChange={(e) => setBusinessInfo(prev => ({ ...prev, address: e.target.value }))}
                fullWidth
                required
                multiline
                rows={3}
                className="kyc-form-field"
                margin="normal"
                      />
              <TextField
                label="Website (Optional)"
                value={businessInfo.website}
                onChange={(e) => setBusinessInfo(prev => ({ ...prev, website: e.target.value }))}
                fullWidth
                className="kyc-form-field"
                margin="normal"
              />
              <TextField
                label="Business Type / Legal Structure *"
                placeholder="e.g., LLC, Pvt Ltd, Sole Proprietorship"
                value={businessInfo.businessType}
                onChange={(e) => setBusinessInfo(prev => ({ ...prev, businessType: e.target.value }))}
                fullWidth
                required
                className="kyc-form-field"
                margin="normal"
              />
              <TextField
                label="Industry / Nature of Business (NACE/NAICS) *"
                placeholder="e.g., 522320 - Financial Transactions Processing"
                value={businessInfo.industryCode}
                onChange={(e) => setBusinessInfo(prev => ({ ...prev, industryCode: e.target.value }))}
                fullWidth
                required
                className="kyc-form-field"
                margin="normal"
              />
              <TextField
                label="Year of Incorporation *"
                placeholder="e.g., 2015"
                type="number"
                inputProps={{ min: 1800, max: new Date().getFullYear() }}
                value={businessInfo.yearOfIncorporation}
                onChange={(e) => setBusinessInfo(prev => ({ ...prev, yearOfIncorporation: e.target.value }))}
                fullWidth
                required
                className="kyc-form-field"
                margin="normal"
              />
              <TextField
                label="Number of Employees *"
                type="number"
                inputProps={{ min: 1 }}
                placeholder="e.g., 25"
                value={businessInfo.numberOfEmployees}
                onChange={(e) => setBusinessInfo(prev => ({ ...prev, numberOfEmployees: e.target.value }))}
                fullWidth
                required
                className="kyc-form-field"
                margin="normal"
              />
              <TextField
                label="Business Description *"
                placeholder="Briefly describe the products or services your business provides"
                value={businessInfo.businessDescription}
                onChange={(e) => setBusinessInfo(prev => ({ ...prev, businessDescription: e.target.value }))}
                fullWidth
                required
                multiline
                rows={4}
                className="kyc-form-field"
                margin="normal"
              />
            </div>
          </div>
        );

      case 1:
        return (
          <div className="kyc-step-content">
            <h3 className="kyc-step-title">Upload Corporate Documents</h3>
            <p className="kyc-step-subtitle">Upload required corporate documents</p>
            <p className="kyc-required-note">
              <span className="kyc-required-asterisk">*</span> All documents are required. Maximum file size is {DOCUMENT_MAX_SIZE_MB}MB per document.
            </p>
            
            <div className="kyc-document-upload-section" style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              {PRIMARY_CORPORATE_DOC_TYPES.map((docType) => {
                const docState = documents[docType];
                const formattedLabel = formatDocumentLabel(docType);
                // Check both local state and fetched documents (handle case-insensitive matching)
                const isUploaded = isDocumentProvided(docType);
                
            return (
                    <div key={docType} className="kyc-document-upload-item">
                    <h5>
                      {formattedLabel}
                      <span className="kyc-required-asterisk">*</span>
                    </h5>
                    <p className="kyc-document-helper">Accepted formats: PDF or image</p>
                    {isUploaded ? (
                      <>
                        <div className="kyc-document-uploaded">
                          <span>✓ Uploaded</span>
                          {docState?.uploadStatus === 'error' && (
                            <span style={{ color: 'red', marginLeft: '10px' }}>Upload failed, please try again</span>
                          )}
                        </div>
                        {renderDocumentInlineActions(docType)}
                      </>
                    ) : (
                      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                        <label style={{ 
                          padding: '12px 24px',
                          backgroundColor: 'var(--primary-color, #11BE6A)',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '0.95rem',
                          fontWeight: 500,
                          textAlign: 'center',
                          width: '100%',
                          maxWidth: '300px',
                          transition: 'all 0.3s ease'
                        }}>
                          Choose File
                          <input
                            type="file"
                            accept="image/*,.pdf"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleDocumentUpload(docType, file);
                              // Reset input so same file can be selected again if needed
                              e.target.value = '';
                }}
                            disabled={docState?.uploadStatus === 'uploading'}
                            style={{ display: 'none' }}
                          />
                        </label>
                        {docState?.uploadStatus === 'uploading' && (
                          <p style={{ color: 'var(--body_color)', margin: 0 }}>Uploading...</p>
                        )}
                        {docState?.uploadStatus === 'error' && (
                          <p style={{ color: '#dc3545', margin: 0 }}>Upload failed. Please try again.</p>
                        )}
            </div>
                    )}
          </div>
        );
              })}
            </div>
          </div>
        );

      case 2:
          return (
            <div className="kyc-step-content">
            <h3 className="kyc-step-title">Directors & UBOs</h3>
            <p className="kyc-step-subtitle">Add directors and ultimate beneficial owners</p>
            
            <div className="kyc-directors-section">
              <h4>Directors ({directors.length})</h4>
              {!showDirectorForm ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'center', width: '100%', marginBottom: '20px' }}>
                    <GenericButton
                      text="Add Director"
                      onClick={() => setShowDirectorForm(true)}
                      className="kyc-add-button"
                      IconComponent={undefined}
                      disabled={false}
                      loading={false}
                      styles={undefined}
                    />
                  </div>
                  <div className="kyc-directors-list">
                    {directors.length === 0 ? (
                      <p style={{ color: 'var(--body_color)', opacity: 0.7, marginTop: '10px', textAlign: 'center' }}>
                        No directors added yet. Click "Add Director" to add one.
                      </p>
                    ) : (
                      directors.map((director) => (
                        <div key={director._id || Math.random()} className="kyc-director-item">
                          <span>
                            {director.firstName} {director.lastName} - {director.position}
                            {typeof director.ownershipPercentage === 'number' && director.ownershipPercentage > 0 && ` (${director.ownershipPercentage}% ownership)`}
                            {' • '}
                            PEP: {(director.pepStatus || 'no').toUpperCase()} | Sanctions: {(director.sanctionsStatus || 'no').toUpperCase()}
                          </span>
                          <GenericButton
                            text="Delete"
                            onClick={() => director._id && handleDeleteDirector(director._id)}
                            className="kyc-delete-button"
                            IconComponent={undefined}
                            disabled={false}
                            styles={undefined}
                            loading={false}
                          />
                      </div>
                      ))
                    )}
                  </div>
                </>
              ) : (
                <div className="kyc-form-section">
                  <TextField
                    label="First Name *"
                    value={directorForm.firstName}
                    onChange={(e) => setDirectorForm(prev => ({ ...prev, firstName: e.target.value }))}
                    fullWidth
                    margin="normal"
                    />
                  <TextField
                    label="Last Name *"
                    value={directorForm.lastName}
                    onChange={(e) => setDirectorForm(prev => ({ ...prev, lastName: e.target.value }))}
                    fullWidth
                    margin="normal"
                  />
                  <TextField
                    label="Email *"
                    value={directorForm.email}
                    onChange={(e) => setDirectorForm(prev => ({ ...prev, email: e.target.value }))}
                    fullWidth
                    margin="normal"
                  />
                  <TextField
                    label="Phone"
                    value={directorForm.phone}
                    onChange={(e) => setDirectorForm(prev => ({ ...prev, phone: e.target.value }))}
                    fullWidth
                    margin="normal"
                  />
                  <TextField
                    label="Position *"
                    value={directorForm.position}
                    onChange={(e) => setDirectorForm(prev => ({ ...prev, position: e.target.value }))}
                    fullWidth
                    margin="normal"
                  />
                  <TextField
                    label="Date of Birth *"
                    type="date"
                    value={directorForm.dateOfBirth}
                    onChange={(e) => setDirectorForm(prev => ({ ...prev, dateOfBirth: e.target.value }))}
                    fullWidth
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="Nationality *"
                    value={directorForm.nationality}
                    onChange={(e) => setDirectorForm(prev => ({ ...prev, nationality: e.target.value }))}
                    fullWidth
                    margin="normal"
                  />
                  <TextField
                    label="Country of Residence *"
                    value={directorForm.countryOfResidence}
                    onChange={(e) => setDirectorForm(prev => ({ ...prev, countryOfResidence: e.target.value }))}
                    fullWidth
                    margin="normal"
                  />
                  <TextField
                    label="Residential Address *"
                    value={directorForm.address}
                    onChange={(e) => setDirectorForm(prev => ({ ...prev, address: e.target.value }))}
                    fullWidth
                    margin="normal"
                    multiline
                    rows={3}
                  />
                  <TextField
                    label="Percentage Ownership"
                    type="number"
                    value={directorForm.ownershipPercentage || 0}
                    onChange={(e) => setDirectorForm(prev => ({ ...prev, ownershipPercentage: parseFloat(e.target.value) || 0 }))}
                    fullWidth
                    margin="normal"
                  />
                  <TextField
                    label="ID Document Type *"
                    value={directorForm.idDocumentType || 'passport'}
                    onChange={(e) => setDirectorForm(prev => ({ ...prev, idDocumentType: e.target.value }))}
                    fullWidth
                    margin="normal"
                    select
                    SelectProps={{ native: true }}
                  >
                    <option value="passport">Passport</option>
                    <option value="national_id">National ID</option>
                    <option value="driving_license">Driving License</option>
                    <option value="other">Other</option>
                  </TextField>

                  <div className="kyc-inline-upload">
                    <p className="kyc-upload-label">ID Document (Front) *</p>
                    <label className="kyc-upload-button">
                      Upload Front
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          try {
                            const base64 = await fileToBase64(file);
                            setDirectorForm(prev => ({ ...prev, idDocumentFront: base64 }));
                            OpenNotification('success', 'Front ID document ready to submit.');
                          } catch (error) {
                            console.error('Failed to process file', error);
                            OpenNotification('error', 'Unable to read file. Please try again with a different image.');
                          } finally {
                            e.target.value = '';
                          }
                        }}
                        style={{ display: 'none' }}
                      />
                    </label>
                    {directorForm.idDocumentFront && <small className="kyc-upload-success">Front document attached</small>}
                  </div>

                  <div className="kyc-inline-upload">
                    <p className="kyc-upload-label">ID Document (Back)</p>
                    <label className="kyc-upload-button">
                      Upload Back
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          try {
                            const base64 = await fileToBase64(file);
                            setDirectorForm(prev => ({ ...prev, idDocumentBack: base64 }));
                            OpenNotification('success', 'Back of ID document ready to submit.');
                          } catch (error) {
                            console.error('Failed to process file', error);
                            OpenNotification('error', 'Unable to read file. Please try again with a different image.');
                          } finally {
                            e.target.value = '';
                          }
                        }}
                        style={{ display: 'none' }}
                      />
                    </label>
                    {directorForm.idDocumentBack && <small className="kyc-upload-success">Back document attached</small>}
                  </div>

                  <div className="kyc-inline-upload">
                    <p className="kyc-upload-label">Selfie / Liveness Check (optional)</p>
                    <label className="kyc-upload-button">
                      Upload Selfie
                      <input
                        type="file"
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          try {
                            const base64 = await fileToBase64(file);
                            setDirectorForm(prev => ({ ...prev, selfieImage: base64 }));
                            OpenNotification('success', 'Selfie captured successfully.');
                          } catch (error) {
                            console.error('Failed to process selfie', error);
                            OpenNotification('error', 'Unable to read selfie file. Please try again.');
                          } finally {
                            e.target.value = '';
                          }
                        }}
                        style={{ display: 'none' }}
                      />
                    </label>
                    {directorForm.selfieImage && <small className="kyc-upload-success">Selfie attached</small>}
                  </div>

                  <TextField
                    label="Politically Exposed Person (PEP)? *"
                    value={directorForm.pepStatus || 'no'}
                    onChange={(e) => setDirectorForm(prev => ({ ...prev, pepStatus: e.target.value as 'yes' | 'no' }))}
                    fullWidth
                    margin="normal"
                    select
                    SelectProps={{ native: true }}
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </TextField>

                  <TextField
                    label="On Sanctions List? (OFAC/EU/UK) *"
                    value={directorForm.sanctionsStatus || 'no'}
                    onChange={(e) => setDirectorForm(prev => ({ ...prev, sanctionsStatus: e.target.value as 'yes' | 'no' }))}
                    fullWidth
                    margin="normal"
                    select
                    SelectProps={{ native: true }}
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </TextField>
                  <div className="kyc-form-actions">
                    <GenericButton
                      text="Save"
                      onClick={handleAddDirector}
                      loading={loading}
                      className="kyc-save-button"
                      IconComponent={undefined}
                      disabled={false}
                      styles={undefined}
                    />
                    <GenericButton
                      text="Cancel"
                      onClick={() => {
                        setShowDirectorForm(false);
                        setDirectorForm({
                          firstName: '',
                          lastName: '',
                          email: '',
                          phone: '',
                          dateOfBirth: '',
                          nationality: '',
                          address: '',
                          countryOfResidence: '',
                          position: '',
                          ownershipPercentage: 0,
                          idDocumentType: 'passport',
                          idDocumentFront: '',
                          idDocumentBack: '',
                          selfieImage: '',
                          pepStatus: 'no',
                          sanctionsStatus: 'no',
                        });
                      }}
                      className="kyc-cancel-button"
                      IconComponent={undefined}
                      disabled={false}
                      loading={false}
                      styles={undefined}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="kyc-ubos-section">
              <h4>UBOs ({ubos.length})</h4>
              {!showUboForm ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'center', width: '100%', marginBottom: '20px' }}>
                      <GenericButton
                      text="Add UBO"
                      onClick={() => setShowUboForm(true)}
                      className="kyc-add-button"
                      IconComponent={undefined}
                      disabled={false}
                      loading={false}
                      styles={undefined}
                      />
                    </div>
                  <div className="kyc-ubos-list">
                    {ubos.length === 0 ? (
                      <p style={{ color: 'var(--body_color)', opacity: 0.7, marginTop: '10px', textAlign: 'center' }}>
                        No UBOs added yet. Click "Add UBO" to add one.
                      </p>
                    ) : (
                      ubos.map((ubo) => (
                        <div key={ubo._id || Math.random()} className="kyc-ubo-item">
                          <span>
                            {ubo.firstName} {ubo.lastName} - {ubo.ownershipPercentage}% ownership - KYC: {ubo.kycStatus || 'pending'}
                            {' • '} PEP: {(ubo.pepStatus || 'no').toUpperCase()} | Sanctions: {(ubo.sanctionsStatus || 'no').toUpperCase()}
                          </span>
                          <GenericButton
                            text="Delete"
                            onClick={() => ubo._id && handleDeleteUBO(ubo._id)}
                            className="kyc-delete-button"
                            IconComponent={undefined}
                            disabled={false}
                            loading={false}
                            styles={undefined}
                          />
                        </div>
                      ))
                      )}
                    </div>
                </>
              ) : (
                <div className="kyc-form-section">
                  <TextField
                    label="First Name *"
                    value={uboForm.firstName}
                    onChange={(e) => setUboForm(prev => ({ ...prev, firstName: e.target.value }))}
                    fullWidth
                    margin="normal"
                  />
                  <TextField
                    label="Last Name *"
                    value={uboForm.lastName}
                    onChange={(e) => setUboForm(prev => ({ ...prev, lastName: e.target.value }))}
                    fullWidth
                    margin="normal"
                  />
                  <TextField
                    label="Email *"
                    value={uboForm.email}
                    onChange={(e) => setUboForm(prev => ({ ...prev, email: e.target.value }))}
                    fullWidth
                    margin="normal"
                  />
                  <TextField
                    label="Phone"
                    value={uboForm.phone}
                    onChange={(e) => setUboForm(prev => ({ ...prev, phone: e.target.value }))}
                    fullWidth
                    margin="normal"
                  />
                  <TextField
                    label="Date of Birth *"
                    type="date"
                    value={uboForm.dateOfBirth}
                    onChange={(e) => setUboForm(prev => ({ ...prev, dateOfBirth: e.target.value }))}
                    fullWidth
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="Nationality *"
                    value={uboForm.nationality}
                    onChange={(e) => setUboForm(prev => ({ ...prev, nationality: e.target.value }))}
                    fullWidth
                    margin="normal"
                  />
                  <TextField
                    label="Country of Residence *"
                    value={uboForm.countryOfResidence}
                    onChange={(e) => setUboForm(prev => ({ ...prev, countryOfResidence: e.target.value }))}
                    fullWidth
                    margin="normal"
                  />
                  <TextField
                    label="Residential Address *"
                    value={uboForm.address}
                    onChange={(e) => setUboForm(prev => ({ ...prev, address: e.target.value }))}
                    fullWidth
                    margin="normal"
                    multiline
                    rows={3}
                  />
                  <TextField
                    label="Ownership Percentage *"
                    type="number"
                    value={uboForm.ownershipPercentage}
                    onChange={(e) => setUboForm(prev => ({ ...prev, ownershipPercentage: parseFloat(e.target.value) || 0 }))}
                    fullWidth
                    margin="normal"
                  />
                  <TextField
                    label="Relationship to Company *"
                    value={uboForm.relationshipToCompany}
                    onChange={(e) => setUboForm(prev => ({ ...prev, relationshipToCompany: e.target.value }))}
                    fullWidth
                    margin="normal"
                    select
                    SelectProps={{ native: true }}
                  >
                    <option value="Shareholder">Shareholder</option>
                    <option value="Beneficial Owner">Beneficial Owner</option>
                    <option value="Trust Beneficiary">Trust Beneficiary</option>
                  </TextField>
                  <TextField
                    label="ID Document Type *"
                    value={uboForm.idDocumentType || 'passport'}
                    onChange={(e) => setUboForm(prev => ({ ...prev, idDocumentType: e.target.value }))}
                    fullWidth
                    margin="normal"
                    select
                    SelectProps={{ native: true }}
                  >
                    <option value="passport">Passport</option>
                    <option value="national_id">National ID</option>
                    <option value="driving_license">Driving License</option>
                    <option value="other">Other</option>
                  </TextField>

                  <div className="kyc-inline-upload">
                    <p className="kyc-upload-label">ID Document (Front) *</p>
                    <label className="kyc-upload-button">
                      Upload Front
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          try {
                            const base64 = await fileToBase64(file);
                            setUboForm(prev => ({ ...prev, idDocumentFront: base64 }));
                            OpenNotification('success', 'UBO front document ready.');
                          } catch (error) {
                            console.error('Failed to process UBO document', error);
                            OpenNotification('error', 'Unable to read file. Please try again.');
                          } finally {
                            e.target.value = '';
                          }
                        }}
                        style={{ display: 'none' }}
                      />
                    </label>
                    {uboForm.idDocumentFront && <small className="kyc-upload-success">Front document attached</small>}
                  </div>

                  <div className="kyc-inline-upload">
                    <p className="kyc-upload-label">ID Document (Back)</p>
                    <label className="kyc-upload-button">
                      Upload Back
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          try {
                            const base64 = await fileToBase64(file);
                            setUboForm(prev => ({ ...prev, idDocumentBack: base64 }));
                            OpenNotification('success', 'UBO back document ready.');
                          } catch (error) {
                            console.error('Failed to process UBO document', error);
                            OpenNotification('error', 'Unable to read file. Please try again.');
                          } finally {
                            e.target.value = '';
                          }
                        }}
                        style={{ display: 'none' }}
                      />
                    </label>
                    {uboForm.idDocumentBack && <small className="kyc-upload-success">Back document attached</small>}
                  </div>

                  <div className="kyc-inline-upload">
                    <p className="kyc-upload-label">Selfie / Liveness Check (optional)</p>
                    <label className="kyc-upload-button">
                      Upload Selfie
                      <input
                        type="file"
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          try {
                            const base64 = await fileToBase64(file);
                            setUboForm(prev => ({ ...prev, selfieImage: base64 }));
                            OpenNotification('success', 'UBO selfie attached.');
                          } catch (error) {
                            console.error('Failed to process UBO selfie', error);
                            OpenNotification('error', 'Unable to read selfie. Please try again.');
                          } finally {
                            e.target.value = '';
                          }
                        }}
                        style={{ display: 'none' }}
                      />
                    </label>
                    {uboForm.selfieImage && <small className="kyc-upload-success">Selfie attached</small>}
                  </div>

                  <TextField
                    label="Politically Exposed Person (PEP)? *"
                    value={uboForm.pepStatus || 'no'}
                    onChange={(e) => setUboForm(prev => ({ ...prev, pepStatus: e.target.value as 'yes' | 'no' }))}
                    fullWidth
                    margin="normal"
                    select
                    SelectProps={{ native: true }}
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </TextField>

                  <TextField
                    label="On Sanctions List? (OFAC/EU/UK) *"
                    value={uboForm.sanctionsStatus || 'no'}
                    onChange={(e) => setUboForm(prev => ({ ...prev, sanctionsStatus: e.target.value as 'yes' | 'no' }))}
                    fullWidth
                    margin="normal"
                    select
                    SelectProps={{ native: true }}
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </TextField>
                  <div className="kyc-form-actions">
                      <GenericButton
                      text="Save"
                      onClick={handleAddUBO}
                      loading={loading}
                      className="kyc-save-button"
                      IconComponent={undefined}
                      disabled={false}
                      styles={undefined}
                      />
                      <GenericButton
                      text="Cancel"
                        onClick={() => {
                        setShowUboForm(false);
                        setUboForm({
                          firstName: '',
                          lastName: '',
                          email: '',
                          phone: '',
                          dateOfBirth: '',
                          nationality: '',
                          address: '',
                          countryOfResidence: '',
                          ownershipPercentage: 0,
                          relationshipToCompany: 'Shareholder',
                          idDocumentType: 'passport',
                          idDocumentFront: '',
                          idDocumentBack: '',
                          selfieImage: '',
                          pepStatus: 'no',
                          sanctionsStatus: 'no',
                        });
                        }}
                      className="kyc-cancel-button"
                      IconComponent={undefined}
                      disabled={false}
                      loading={false}
                      styles={undefined}
                      />
                    </div>
                </div>
                )}
            </div>
              </div>
            );

      case 3:
            return (
              <div className="kyc-step-content">
            <h3 className="kyc-step-title">Tax Information</h3>
            <p className="kyc-step-subtitle">Provide your business tax identification details</p>
            
            <div className="kyc-form-section">
              <TextField
                label="Tax Identification Number *"
                value={taxForm.taxIdentificationNumber}
                onChange={(e) => setTaxForm(prev => ({ ...prev, taxIdentificationNumber: e.target.value }))}
                fullWidth
                required
                className="kyc-form-field"
                margin="normal"
                    />
              <TextField
                label="Tax Country *"
                value={taxForm.taxCountry}
                onChange={(e) => setTaxForm(prev => ({ ...prev, taxCountry: e.target.value }))}
                fullWidth
                required
                className="kyc-form-field"
                margin="normal"
                placeholder="e.g., US, UK, PK"
                      />
              <TextField
                label="VAT Number"
                value={taxForm.vatNumber}
                onChange={(e) => setTaxForm(prev => ({ ...prev, vatNumber: e.target.value }))}
                fullWidth
                className="kyc-form-field"
                margin="normal"
                placeholder="e.g., GB123456789"
              />
              <TextField
                label="FATCA Status *"
                value={taxForm.fatcaStatus}
                onChange={(e) => setTaxForm(prev => ({ ...prev, fatcaStatus: e.target.value as 'applicable' | 'not_applicable' | 'unsure' }))}
                fullWidth
                required
                className="kyc-form-field"
                margin="normal"
                select
                SelectProps={{ native: true }}
                helperText="Required only for U.S.-linked companies"
              >
                <option value="not_applicable">Not Applicable</option>
                <option value="applicable">Subject to FATCA</option>
                <option value="unsure">Unsure / Need Assistance</option>
              </TextField>
                    </div>
              </div>
            );

      case 4:
            return (
              <div className="kyc-step-content">
            <h3 className="kyc-step-title">Business Bank Statement</h3>
            <p className="kyc-step-subtitle">Upload your business bank statement</p>
            
            <div className="kyc-document-upload-section">
              <div className="kyc-document-upload-item">
                <h5>Company Bank Statement</h5>
                {(documents.COMPANY_BANK_STATEMENT?.uploadStatus === 'success' || 
                  fetchedDocuments.some(doc => doc.type === 'COMPANY_BANK_STATEMENT')) ? (
                  <div className="kyc-document-uploaded">
                    <span>✓ Uploaded</span>
                  </div>
                ) : (
                  <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                    <label style={{ 
                      padding: '12px 24px',
                      backgroundColor: 'var(--primary-color, #11BE6A)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '0.95rem',
                      fontWeight: 500,
                      textAlign: 'center',
                      width: '100%',
                      maxWidth: '300px',
                      transition: 'all 0.3s ease'
                    }}>
                      Choose File
                    <input
                      type="file"
                        accept="image/*,.pdf"
                      onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleDocumentUpload('COMPANY_BANK_STATEMENT', file);
                      }}
                        disabled={documents.COMPANY_BANK_STATEMENT?.uploadStatus === 'uploading'}
                      style={{ display: 'none' }}
                    />
                    </label>
                    {documents.COMPANY_BANK_STATEMENT?.uploadStatus === 'uploading' && (
                      <p style={{ color: 'var(--body_color)', margin: 0 }}>Uploading...</p>
                )}
              </div>
                  )}
              </div>
            </div>
            {renderDocumentInlineActions('COMPANY_BANK_STATEMENT')}
          </div>
          );

      case 5:
        return (
          <div className="kyc-step-content">
            <h3 className="kyc-step-title">AML & Compliance Questions</h3>
            <p className="kyc-step-subtitle">Mandatory KYB AML questionnaire for compliance review</p>

            <div className="kyc-form-section">
              <TextField
                label="Purpose of using our platform *"
                value={complianceForm.purposeOfUse}
                onChange={(e) => setComplianceForm(prev => ({ ...prev, purposeOfUse: e.target.value }))}
                fullWidth
                required
                multiline
                rows={3}
                className="kyc-form-field"
                margin="normal"
              />
              <TextField
                label="Expected monthly trading volume (USD) *"
                value={complianceForm.expectedMonthlyVolume}
                onChange={(e) => setComplianceForm(prev => ({ ...prev, expectedMonthlyVolume: e.target.value }))}
                fullWidth
                required
                className="kyc-form-field"
                margin="normal"
              />
              <TextField
                label="Expected number of transactions per month *"
                value={complianceForm.expectedTransactionCount}
                onChange={(e) => setComplianceForm(prev => ({ ...prev, expectedTransactionCount: e.target.value }))}
                fullWidth
                required
                className="kyc-form-field"
                margin="normal"
              />
              <TextField
                label="Expected size of transactions *"
                value={complianceForm.expectedTransactionSize}
                onChange={(e) => setComplianceForm(prev => ({ ...prev, expectedTransactionSize: e.target.value }))}
                fullWidth
                required
                className="kyc-form-field"
                margin="normal"
              />
              <TextField
                label="Source of funds *"
                value={complianceForm.sourceOfFunds}
                onChange={(e) => setComplianceForm(prev => ({ ...prev, sourceOfFunds: e.target.value }))}
                fullWidth
                required
                multiline
                rows={3}
                className="kyc-form-field"
                margin="normal"
              />
              <TextField
                label="Source of wealth *"
                value={complianceForm.sourceOfWealth}
                onChange={(e) => setComplianceForm(prev => ({ ...prev, sourceOfWealth: e.target.value }))}
                fullWidth
                required
                multiline
                rows={3}
                className="kyc-form-field"
                margin="normal"
              />
              <TextField
                label="Does the company deal with crypto? *"
                value={complianceForm.dealsWithCrypto}
                onChange={(e) => setComplianceForm(prev => ({ ...prev, dealsWithCrypto: e.target.value }))}
                fullWidth
                required
                select
                SelectProps={{ native: true }}
                className="kyc-form-field"
                margin="normal"
              >
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </TextField>
              <TextField
                label="Does the company deal with cash-intensive industries? *"
                value={complianceForm.dealsWithCashIntensive}
                onChange={(e) => setComplianceForm(prev => ({ ...prev, dealsWithCashIntensive: e.target.value }))}
                fullWidth
                required
                select
                SelectProps={{ native: true }}
                className="kyc-form-field"
                margin="normal"
              >
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </TextField>
              <TextField
                label="Does the company deal with high-risk countries? *"
                value={complianceForm.dealsWithHighRiskCountries}
                onChange={(e) => setComplianceForm(prev => ({ ...prev, dealsWithHighRiskCountries: e.target.value }))}
                fullWidth
                required
                select
                SelectProps={{ native: true }}
                className="kyc-form-field"
                margin="normal"
              >
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </TextField>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="kyc-step-content">
            <h3 className="kyc-step-title">Review & Submit</h3>
            <p className="kyc-step-subtitle">Review all information before submitting</p>
            
            <div className="kyc-review-section">
              <div className="kyc-review-item">
                <h4>Business Information</h4>
                <p>Legal Name: {businessInfo.legalName}</p>
                <p>Registration Number: {businessInfo.registrationNumber}</p>
                <p>Country: {businessInfo.countryOfIncorporation}</p>
              </div>
              
              <div className="kyc-review-item">
                <h4>Documents ({fetchedDocuments.length})</h4>
                <p>Corporate documents uploaded</p>
              </div>
              
              <div className="kyc-review-item">
                <h4>Directors ({directors.length})</h4>
                {directors.map(d => (
                  <p key={d._id}>{d.firstName} {d.lastName} - {d.position}</p>
                ))}
              </div>
              
              <div className="kyc-review-item">
                <h4>UBOs ({ubos.length})</h4>
                {ubos.map(u => (
                  <p key={u._id}>{u.firstName} {u.lastName} - {u.ownershipPercentage}% - KYC: {u.kycStatus || 'pending'}</p>
                ))}
              </div>
              
              {taxInfo && (
                <div className="kyc-review-item">
                  <h4>Tax Information</h4>
                  <p>TIN: {taxInfo.taxIdentificationNumber}</p>
                  <p>Country: {taxInfo.taxCountry}</p>
                  {taxInfo.vatNumber && <p>VAT: {taxInfo.vatNumber}</p>}
                  <p>FATCA Status: {(taxInfo.fatcaStatus || 'not_applicable').replace(/_/g, ' ').toUpperCase()}</p>
                </div>
              )}

              {(complianceInfo || complianceForm.purposeOfUse) && (
                <div className="kyc-review-item">
                  <h4>AML & Compliance</h4>
                  <p>Purpose: {(complianceInfo?.purposeOfUse || complianceForm.purposeOfUse) || 'Not provided'}</p>
                  <p>Monthly Volume: {(complianceInfo?.expectedMonthlyVolume || complianceForm.expectedMonthlyVolume) || 'Not provided'}</p>
                  <p>
                    Transactions: {(complianceInfo?.expectedTransactionCount || complianceForm.expectedTransactionCount) || 'Not provided'} @ {(complianceInfo?.expectedTransactionSize || complianceForm.expectedTransactionSize) || 'Not provided'}
                  </p>
                  <p>Source of Funds: {(complianceInfo?.sourceOfFunds || complianceForm.sourceOfFunds) || 'Not provided'}</p>
                  <p>Source of Wealth: {(complianceInfo?.sourceOfWealth || complianceForm.sourceOfWealth) || 'Not provided'}</p>
                  <p>
                    Deals with Crypto: {(complianceInfo ? (complianceInfo.dealsWithCrypto ? 'YES' : 'NO') : complianceForm.dealsWithCrypto.toUpperCase())}
                  </p>
                  <p>
                    Cash-Intensive Industries: {(complianceInfo ? (complianceInfo.dealsWithCashIntensive ? 'YES' : 'NO') : complianceForm.dealsWithCashIntensive.toUpperCase())}
                  </p>
                  <p>
                    High-Risk Countries: {(complianceInfo ? (complianceInfo.dealsWithHighRiskCountries ? 'YES' : 'NO') : complianceForm.dealsWithHighRiskCountries.toUpperCase())}
                  </p>
                </div>
              )}
            </div>
            
            <div className="kyc-warning-box">
              <p>⚠️ Please confirm all data is correct. Once submitted, changes may be limited.</p>
            </div>
          </div>
        );

      case 7:
        return (
          <div className="kyc-step-content">
            <h3 className="kyc-step-title">KYB Verification Status</h3>
            {kybStatus?.application && (
              <div className="kyc-status-container">
                <div className={`kyc-status-badge kyc-status-${kybStatus.application.status || 'pending'}`}>
                  {kybStatus.application.status
                    ? kybStatus.application.status.replace(/_/g, ' ').toUpperCase()
                    : 'STATUS'}
                </div>
                {(kybStatus.application.status === 'pending' || kybStatus.application.status === 'under_review') && (
                  <div className="kyc-status-message">
                    <h4>Application Under Review</h4>
                    <p>Your KYB application is currently under review. Please wait for approval.</p>
                  </div>
                )}
                {kybStatus.application.status === 'rejected' && (
                  <div className="kyc-rejection-reason">
                    <p className="kyc-rejection-title">Rejection Reason:</p>
                    <p className="kyc-rejection-text">
                      {kybStatus.application.rejectionReason || 'No specific reason provided.'}
                    </p>
                    <p className="kyc-reapply-message">
                      You can resubmit your KYB application immediately.
                    </p>
                    <GenericButton
                      text="Resubmit KYB"
                      onClick={handleReapply}
                      IconComponent={undefined}
                      loading={false}
                      styles={undefined}
                    />
                  </div>
                )}
                {kybStatus.application.status === 'need_more_info' && (
                  <div className="kyc-need-more-info">
                    <p>Additional information is required. Please review and update your application.</p>
                    <GenericButton
                      text="Edit Application"
                      onClick={() => setActiveStep(0)}
                      className="kyc-edit-button"
                      IconComponent={undefined}
                      disabled={false}
                      loading={false}
                      styles={undefined}
                    />
                  </div>
                )}
                {kybStatus.application.status === 'approved' && (
                  <div className="kyc-approved">
                    <h4>✓ KYB Verification Approved!</h4>
                    <p>
                      Your business verification has been approved successfully. You now have access to advanced business features.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="kyc-verification-container" data-verification-type="kyb">
      <div className="kyc-header">
        <h1 className="kyc-main-title">Business Verification (KYB)</h1>
        <p className="kyc-subtitle">Complete Know Your Business verification to unlock business features</p>
      </div>

      <Box className="kyc-stepper-container">
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <Card className="kyc-card">
        <Card.Body className="kyc-card-body">
          {renderStepContent()}

          {activeStep === 0 && (
            <div className="kyc-actions kyc-actions-center">
              <GenericButton
                text="Save & Continue"
                onClick={handleNext}
                loading={loading}
                className="kyc-next-button"
                IconComponent={undefined}
                disabled={false}
                styles={undefined}
              />
            </div>
          )}
          {activeStep > 0 && activeStep < 7 && (
            <div className="kyc-actions kyc-actions-with-back">
                <GenericButton
                  text="Back"
                  onClick={handleBack}
                  className="kyc-back-button kyc-back-button-outline"
                IconComponent={undefined}
                disabled={false}
                loading={false}
                styles={undefined}
                />
                <GenericButton
                text={activeStep === 6 ? "Submit for Review" : "Next"}
                  onClick={handleNext}
                  loading={loading}
                  className="kyc-next-button"
                IconComponent={undefined}
                disabled={false}
                styles={undefined}
              />
            </div>
          )}
          {activeStep === 7 && (
            <div className="kyc-actions kyc-actions-left">
              <GenericButton
                text="Back to Account"
                onClick={() => navigate('/indexx-exchange/account')}
                className="kyc-back-button"
                IconComponent={undefined}
                disabled={false}
                loading={false}
                styles={undefined}
              />
            </div>
          )}
        </Card.Body>
      </Card>
      {showLoginPopup && <LoginRequiredPopup onClose={handleLoginPopupClose} />}
    </div>
  );
};

export default VerifyWithKyb;
